import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Check, ChevronDown } from 'lucide-react';
import styles from './Select.module.scss';

/* ── Select ─────────────────────────────────────────────────────
 * Single-choice dropdown. The trigger and opened option panel
 * share visual language with MultiSelect / SearchableSelect (soft
 * blue selected row, trailing check, portal popup that escapes
 * ancestor overflow), but the public API stays children-based —
 *   <Select value={x} onChange={e => ...} name="...">
 *     <option value="">Placeholder</option>
 *     <option value="a">A</option>
 *   </Select>
 * so the 60+ existing callers that read `e.target.value` keep
 * working without changes. We synthesize an event-shaped object
 * on selection.
 * ────────────────────────────────────────────────────────────── */

const flattenChildren = (children) => {
  const out = [];
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === React.Fragment) {
      out.push(...flattenChildren(child.props.children));
    } else {
      out.push(child);
    }
  });
  return out;
};

const parseOptions = (children) => {
  return flattenChildren(children)
    .filter((c) => c.type === 'option')
    .map((c) => {
      const rawLabel = c.props.children;
      const label =
        typeof rawLabel === 'string' || typeof rawLabel === 'number'
          ? String(rawLabel)
          : Array.isArray(rawLabel)
          ? rawLabel.join('')
          : '';
      // Mirror native `<option>` behaviour: HTML attributes are always
      // strings, so a native <select>'s `e.target.value` is always a
      // string regardless of what you passed to `value={…}` (e.g. an
      // Array becomes "a,b,c", a number becomes "42"). Callers written
      // against the native select rely on that — Leave.js for example
      // does `compoundValue.split(',')` on the value from a
      // `<option value={Object.values(item)}>`. Coerce here so onChange
      // delivers a string just like the native control would.
      const rawValue = c.props.value;
      const value = rawValue == null ? '' : String(rawValue);
      return {
        value,
        label,
        disabled: !!c.props.disabled,
      };
    });
};

const Select = ({
  label,
  error,
  helperText,
  children,
  id,
  className,
  value,
  onChange,
  name,
  disabled,
  placeholder,
  required,
  ...rest
}) => {
  const [open, setOpen] = useState(false);
  const [popupRect, setPopupRect] = useState(null);
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const popupRef = useRef(null);

  const options = useMemo(() => parseOptions(children), [children]);

  // Treat an option with an empty-string value as a placeholder row,
  // matching the long-established `<option value="">Select X</option>`
  // convention used throughout the codebase.
  const selectedOption = useMemo(() => {
    if (value == null) return null;
    const match = options.find((o) => String(o.value) === String(value));
    if (!match) return null;
    if (match.value === '' || match.value === null) return null;
    return match;
  }, [value, options]);

  const placeholderText = useMemo(() => {
    if (placeholder) return placeholder;
    const empty = options.find((o) => o.value === '' || o.value === null);
    return empty?.label || 'Select…';
  }, [placeholder, options]);

  const updatePopupRect = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const r = trigger.getBoundingClientRect();
    setPopupRect({ top: r.bottom + 4, left: r.left, width: r.width });
  };

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      const inTrigger =
        wrapperRef.current && wrapperRef.current.contains(e.target);
      const inPopup = popupRef.current && popupRef.current.contains(e.target);
      if (!inTrigger && !inPopup) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Lock popup to trigger rect; sync with scroll/resize so it tracks the
  // trigger even when an ancestor (PagePanel, drawer body) scrolls.
  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePopupRect();
    const scrollParents = [];
    let node = triggerRef.current?.parentElement;
    while (node && node !== document.body) {
      const overflowY = window.getComputedStyle(node).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') {
        scrollParents.push(node);
      }
      node = node.parentElement;
    }
    const handler = () => updatePopupRect();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    scrollParents.forEach((n) => n.addEventListener('scroll', handler));
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
      scrollParents.forEach((n) => n.removeEventListener('scroll', handler));
    };
  }, [open]);

  const pick = (optValue) => {
    if (onChange) {
      // Synthesize a native-select-style event so callers can keep
      // doing `e.target.value` / `e.target.name`.
      onChange({
        target: { name: name || '', value: optValue, type: 'select-one' },
        currentTarget: { name: name || '', value: optValue },
      });
    }
    setOpen(false);
  };

  const triggerClasses = [
    styles.trigger,
    open ? styles.triggerOpen : '',
    error ? styles.triggerError : '',
    disabled ? styles.triggerDisabled : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  const wrapperClasses = [styles.wrapper].filter(Boolean).join(' ');

  return (
    <div className={wrapperClasses} ref={wrapperRef}>
      {label && (
        <label htmlFor={id} className={styles.label}>
          {label}
        </label>
      )}
      <div
        id={id}
        ref={triggerRef}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-required={required || undefined}
        tabIndex={disabled ? -1 : 0}
        className={triggerClasses}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
          }
        }}
        {...rest}
      >
        <span className={styles.value}>
          {selectedOption ? (
            <span className={styles.valueLabel}>{selectedOption.label}</span>
          ) : (
            <span className={styles.placeholder}>{placeholderText}</span>
          )}
        </span>
        <span
          className={[styles.caret, open ? styles.caretOpen : '']
            .filter(Boolean)
            .join(' ')}
          aria-hidden="true"
        >
          <ChevronDown size={16} />
        </span>
      </div>

      {open && popupRect &&
        createPortal(
          <div
            ref={popupRef}
            className={styles.popup}
            role="listbox"
            style={{
              position: 'fixed',
              top: popupRect.top,
              left: popupRect.left,
              width: popupRect.width,
            }}
          >
            {options.length === 0 ? (
              <div className={styles.empty}>No options</div>
            ) : (
              <ul className={styles.list}>
                {options.map((o, i) => {
                  const checked =
                    selectedOption &&
                    String(selectedOption.value) === String(o.value);
                  const isPlaceholderRow = o.value === '' || o.value === null;
                  const cls = [
                    styles.option,
                    checked ? styles.optionSelected : '',
                    o.disabled ? styles.optionDisabled : '',
                  ]
                    .filter(Boolean)
                    .join(' ');
                  return (
                    <li
                      key={`${o.value}-${i}`}
                      role="option"
                      aria-selected={!!checked}
                      aria-disabled={o.disabled || undefined}
                      className={cls}
                      onClick={() => !o.disabled && pick(o.value)}
                    >
                      <span className={styles.optionBody}>
                        <div
                          className={
                            isPlaceholderRow
                              ? styles.optionPlaceholderLabel
                              : styles.optionLabel
                          }
                        >
                          {o.label}
                        </div>
                      </span>
                      {checked && (
                        <span className={styles.optionCheck} aria-hidden="true">
                          <Check size={14} strokeWidth={3} />
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>,
          document.body
        )}

      {error && <span className={styles.error}>{error}</span>}
      {!error && helperText && <span className={styles.helperText}>{helperText}</span>}
    </div>
  );
};

Select.propTypes = {
  label: PropTypes.string,
  error: PropTypes.string,
  helperText: PropTypes.string,
  children: PropTypes.node.isRequired,
  id: PropTypes.string,
  className: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  name: PropTypes.string,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
};

Select.defaultProps = {
  label: '',
  error: '',
  helperText: '',
  id: undefined,
  className: '',
  value: undefined,
  onChange: undefined,
  name: undefined,
  disabled: false,
  placeholder: '',
  required: false,
};

export default Select;
